/**
 * Pure helpers behind the Saved tab's toolbar: filter groups, filtering,
 * text search and sorting over saved quotes. The Add filter and chips come
 * from resultsView.ts, so Saved and the results share one control.
 */
import dayjs from 'dayjs';

import type { SavedLaneQuote } from '../../../apis/lanePlanner';
import {
  carrierOf,
  comparePrice,
  departureOf,
  MODE_TITLES,
  routeOf,
} from './quoteView';
import { FilterGroup, FilterState } from './resultsView';

export type SavedSort = 'last_added' | 'cheapest' | 'earliest' | 'expiring';

export const SAVED_SORT_OPTIONS: Array<{ value: SavedSort; label: string }> = [
  { value: 'last_added', label: 'Last added' },
  { value: 'cheapest', label: 'Cheapest' },
  { value: 'earliest', label: 'Earliest departure' },
  { value: 'expiring', label: 'Expiring soon' },
];

const VENDOR_NAMES: Record<string, string> = {
  cargo_one: 'Cargo One',
  freightos: 'Freightos',
};

/** A vendor's display name from its code (saved quotes carry only the code). */
export const vendorLabel = (code: string): string =>
  VENDOR_NAMES[code] ??
  code
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** Container size/type codes (20DV, 40HC, ...) of a saved ocean FCL search. */
export const containersOf = (saved: SavedLaneQuote): string[] => {
  // A multi-mode search's request carries every mode's cargo; the containers
  // belong to the ocean quote only.
  if (saved.quote.mode !== 'ocean') return [];
  const ocean = saved.search_params.ocean;
  if (!ocean || ocean.shipment_type !== 'FCL') return [];
  return [
    ...new Set(
      ocean.items
        .map((item) => item.container_size_type)
        .filter((code): code is string => !!code)
    ),
  ];
};

const distinct = (values: string[]): string[] => [...new Set(values)].sort();

/** The filters worth offering for these saved quotes: vendor and mode (2+ each), company (carrier), container. */
export const savedFilterGroups = (saved: SavedLaneQuote[]): FilterGroup[] => {
  const groups: FilterGroup[] = [];
  const vendors = distinct(saved.map((s) => s.quote.vendor));
  if (vendors.length > 1) {
    groups.push({
      key: 'vendor',
      label: 'Vendor',
      options: vendors.map((code) => ({
        value: code,
        label: vendorLabel(code),
      })),
    });
  }
  const modes = distinct(saved.map((s) => s.quote.mode));
  if (modes.length > 1) {
    groups.push({
      key: 'mode',
      label: 'Mode',
      options: modes.map((mode) => ({
        value: mode,
        label: MODE_TITLES[mode as keyof typeof MODE_TITLES],
      })),
    });
  }
  const carriers = distinct(saved.map((s) => carrierOf(s.quote)));
  if (carriers.length > 1) {
    groups.push({
      key: 'carrier',
      label: 'Company',
      options: carriers.map((carrier) => ({ value: carrier, label: carrier })),
    });
  }
  const containers = distinct(saved.flatMap(containersOf));
  if (containers.length > 0) {
    groups.push({
      key: 'container',
      label: 'Container',
      options: containers.map((code) => ({ value: code, label: code })),
    });
  }
  return groups;
};

const MATCHERS: Record<
  string,
  (saved: SavedLaneQuote, values: string[]) => boolean
> = {
  vendor: (saved, values) => values.includes(saved.quote.vendor),
  mode: (saved, values) => values.includes(saved.quote.mode),
  carrier: (saved, values) => values.includes(carrierOf(saved.quote)),
  container: (saved, values) =>
    containersOf(saved).some((code) => values.includes(code)),
};

export const applySavedFilters = (
  saved: SavedLaneQuote[],
  filters: FilterState
): SavedLaneQuote[] => {
  const active = Object.entries(filters).filter(
    ([key, values]) => values.length > 0 && key in MATCHERS
  );
  if (!active.length) return saved;
  return saved.filter((item) =>
    active.every(([key, values]) => MATCHERS[key](item, values))
  );
};

/** Free-text search over carrier, vendor, references and the lane. */
export const searchSaved = (
  saved: SavedLaneQuote[],
  text: string
): SavedLaneQuote[] => {
  const needle = text.trim().toLowerCase();
  if (!needle) return saved;
  return saved.filter((item) =>
    [
      carrierOf(item.quote),
      vendorLabel(item.quote.vendor),
      item.quote.vendor_quote_id,
      item.ref_number ?? '',
      item.origin,
      item.destination,
      routeOf(item.quote) ?? '',
    ].some((field) => field.toLowerCase().includes(needle))
  );
};

const time = (iso: string | null | undefined): number =>
  iso ? new Date(iso).getTime() : Infinity;

/**
 * When a saved quote stops being usable: its validity or the end of its ship
 * day, whichever comes first (the same rule that moves it to Expired).
 */
const expiryOf = (saved: SavedLaneQuote): number => {
  const validUntil = saved.quote.valid_until;
  const valid = validUntil
    ? /^\d{4}-\d{2}-\d{2}$/.test(validUntil.trim())
      ? dayjs(validUntil).endOf('day').valueOf()
      : new Date(validUntil).getTime()
    : Infinity;
  const shipDay = saved.ship_date
    ? dayjs(saved.ship_date).endOf('day').valueOf()
    : Infinity;
  return Math.min(valid, shipDay);
};

/** Ascending by time; two missing values are equal (Infinity - Infinity would be NaN). */
const byTime = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1);

export const sortSaved = (
  saved: SavedLaneQuote[],
  sort: SavedSort
): SavedLaneQuote[] => {
  const sorted = [...saved];
  if (sort === 'cheapest') {
    return sorted.sort((a, b) => comparePrice(a.quote, b.quote));
  }
  if (sort === 'earliest') {
    return sorted.sort(
      (a, b) =>
        byTime(time(departureOf(a.quote)), time(departureOf(b.quote))) ||
        comparePrice(a.quote, b.quote)
    );
  }
  if (sort === 'expiring') {
    return sorted.sort((a, b) => byTime(expiryOf(a), expiryOf(b)));
  }
  // last added: newest first
  return sorted.sort((a, b) => byTime(time(b.created_at), time(a.created_at)));
};
